import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { Observable } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { LocalStoreService } from '../services/local-store.service';
import { streamingApiUrl } from '../services/streaming-config';

// Attaches the Firebase ID token (and the platform's shared request header) to
// our OWN backends only.
//
// Explicitly configured Racebet backend origins qualify, and nothing else:
//   - environment.apiUrl            — the main API;
//   - environment.providers.apiUrl  — the provider gateway (goldenRaceGen2), a
//     separate Cloud Run function whose /launch route is AuthGuard'ed and so
//     needs the very same bearer token (PROVIDER_INTEGRATION_ARCHITECTURE §4.6).
//   - environment.streaming.apiUrl — the authenticated reunion guide/session API, only
//     while streaming is on by the shared gate (services/streaming-config.ts).
//     Media is fetched separately by HLS using its short-lived capability URL.
//
// The gateway is a second host, which is exactly why this interceptor stopped
// being unconditional: a credential must be scoped to the origins that are
// entitled to it, so any future call to a vendor, a CDN or a relative asset
// path leaves without our bearer token or `x-racebet-sec`.
//
// Prefix matching is done on the ORIGIN, not on a raw string startsWith, so a
// look-alike host (`https://api.racebet365.com.evil.tld/…`) cannot pass by
// sharing a prefix with a configured base URL.

const RACEBET_REQUEST_HEADER = 'x-racebet-sec';
const RACEBET_REQUEST_HEADER_VALUE = 'b07d7900017e89e89c9b770e71c7d4a2';

/** Origin of a configured base URL, or '' when it is unset/unparseable. */
export function originOf(baseUrl: string | null | undefined): string {
  if (!baseUrl) {
    return '';
  }
  const match = /^https?:\/\/[^/?#]+/i.exec(String(baseUrl).trim());
  return match ? match[0].toLowerCase() : '';
}

/**
 * True when `url` is served by one of our own backends. Relative URLs (assets,
 * i18n files) and every third-party host are false.
 */
export function isOwnBackendUrl(url: string, origins: string[]): boolean {
  const target = originOf(url);
  if (!target) {
    return false; // relative or non-http(s) — never carries our credentials
  }
  return origins.some((origin) => !!origin && origin === target);
}

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  token: string;

  constructor(private store: LocalStoreService, private auth: AngularFireAuth) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const ownOrigins = [
      originOf(environment.apiUrl),
      originOf(environment.providers && environment.providers.apiUrl),
      // Same gate as ReunionStreamingService (streaming-config.ts): '' when streaming is off.
      originOf(streamingApiUrl(environment)),
    ];

    if (!isOwnBackendUrl(request.url, ownOrigins)) {
      return next.handle(request);
    }

    return this.auth.idToken.pipe(
      take(1),
      switchMap((token: any) => {
        if (token) {
          request = request.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`,
              [RACEBET_REQUEST_HEADER]: RACEBET_REQUEST_HEADER_VALUE,
            },
          });
        } else {
          request = request.clone({
            setHeaders: { [RACEBET_REQUEST_HEADER]: RACEBET_REQUEST_HEADER_VALUE },
          });
        }
        return next.handle(request);
      })
    );
  }
}
